/**
 * Decode HTML entities without executing markup.
 *
 * Saved block attributes and JSON-in-HTML may store characters as entities.
 * Decode once so later escaping/rendering is not double-encoded.
 *
 * @param {string} value Raw value.
 * @return {string} Decoded string.
 */
export function decodeHtmlEntities( value ) {
	const stringValue = String( value ?? '' );

	if ( ! stringValue || stringValue.indexOf( '&' ) === -1 ) {
		return stringValue;
	}

	return stringValue.replace(
		/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi,
		( match, entity ) => {
			const lower = entity.toLowerCase();

			if ( lower[ 0 ] === '#' ) {
				const code =
					lower[ 1 ] === 'x'
						? parseInt( entity.slice( 2 ), 16 )
						: parseInt( entity.slice( 1 ), 10 );

				if ( ! Number.isFinite( code ) || code < 0 ) {
					return match;
				}

				try {
					return String.fromCodePoint( code );
				} catch {
					return match;
				}
			}

			switch ( lower ) {
				case 'amp':
					return '&';
				case 'lt':
					return '<';
				case 'gt':
					return '>';
				case 'quot':
					return '"';
				case 'apos':
					return "'";
				case 'nbsp':
					return '\u00A0';
				default:
					return match;
			}
		}
	);
}

/**
 * Escape text for HTML after decoding any stored entities.
 *
 * @param {string} value Raw value.
 * @return {string} Escaped string.
 */
export function escapeHtml( value ) {
	return decodeHtmlEntities( value )
		.replace( /&/g, '&amp;' )
		.replace( /</g, '&lt;' )
		.replace( />/g, '&gt;' )
		.replace( /"/g, '&quot;' )
		.replace( /'/g, '&#39;' );
}

/**
 * JSON for a script tag: hex-escape HTML-sensitive characters.
 *
 * Prevents React/HTML from turning `&` into `&amp;` inside the payload.
 *
 * @param {*} value Serializable value.
 * @return {string} HTML-safe JSON string.
 */
export function serializeForHtmlJson( value ) {
	return JSON.stringify( value )
		.replace( /</g, '\\u003c' )
		.replace( />/g, '\\u003e' )
		.replace( /&/g, '\\u0026' );
}
